import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import useFormData from '@/hooks/useFormData';
import { useSelector } from 'react-redux';
import { RootState } from '@/types';
import { toast } from 'sonner';
import { useAddVideoMutation } from '@/store/videoApi';
import AllUploads from './AllUploads';
import { useGetBatchQuery } from '@/store/batchApi';

const batchOrder = [
  'HTML',
  'CSS',
  'JavaScript',
  'React',
  'Backend',
  'Basic',
  'Intermediate',
  'Advanced',
];

const UploadSection: React.FC = () => {
  const { formData, handleChange, resetForm } = useFormData({
    title: '',
    url: '',
    batchModule: '',
    batch: '',
  });

  const [activeTab, setActiveTab] = useState('Upload');
  const { user } = useSelector((state: RootState) => state.user);

  const { data: batches } = useGetBatchQuery();
  const [addVideo, { isSuccess }] = useAddVideoMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.title ||
      !formData.url ||
      !formData.batch ||
      !formData.batchModule
    ) {
      return toast.error('Please fill all fields!');
    }

    const res = await addVideo({ ...formData, token: user?.token });
    // console.log(res);
    if (res?.data) {
      toast.success('Video Uploaded Successfully!');
      resetForm();
    } else {
      toast.error(res?.error?.data?.message || 'S0mething went wrong !');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h1>Upload Content</h1>
        </CardTitle>
      </CardHeader>

      <CardContent>
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-6"
        >
          <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
            <TabsTrigger value="Upload">Upload</TabsTrigger>
            <TabsTrigger value="AllUploads">All Uploads</TabsTrigger>
          </TabsList>

          {/* Upload Tab */}
          <TabsContent value="Upload">
            <Card>
              <CardHeader>
                <CardTitle>Fill upload details</CardTitle>
              </CardHeader>

              <CardContent>
                <form className="space-y-4" onSubmit={handleSubmit}>
                  {/* Title */}
                  <div className="space-y-2">
                    <Label htmlFor="title">Content Title</Label>
                    <Input
                      id="title"
                      name="title"
                      placeholder="Enter content title"
                      onChange={handleChange}
                      value={formData.title}
                    />
                  </div>

                  {/* URL */}
                  <div className="space-y-2">
                    <Label htmlFor="url">Enter URL</Label>
                    <Input
                      id="url"
                      name="url"
                      placeholder="Enter or Paste URL"
                      onChange={handleChange}
                      value={formData.url}
                    />
                  </div>

                  {/* Batch */}
                  <div className="space-y-2">
                    <Label htmlFor="batch">Batches</Label>
                    <select
                      id="batch"
                      name="batch"
                      className="border rounded-md px-2 py-2 w-full cursor-pointer bg-background"
                      onChange={handleChange}
                      value={formData.batch}
                    >
                      <option value="">Select Batch</option>
                      {batches?.map((batch, index) => (
                        <option key={index} value={batch._id}>
                          {batch?.bCode}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Batch Module */}
                  <div className="space-y-2">
                    <Label htmlFor="batchModule">Batch Module</Label>
                    <select
                      id="batchModule"
                      name="batchModule"
                      className="border rounded-md px-2 py-2 w-full cursor-pointer bg-background"
                      onChange={handleChange}
                      value={formData.batchModule}
                    >
                      <option value="">Select Module</option>
                      {batchOrder.map((module, index) => (
                        <option key={index} value={module}>
                          {module}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="w-full border-2 rounded p-2 dark:bg-background text-primary-background"
                  >
                    Upload
                  </button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Uploads Tab */}
          <TabsContent value="AllUploads">
            <AllUploads />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default UploadSection;
